import type { SectionViewGeometry } from "building-types/views/View3D";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { planeNew, planeNormal, planeProject, planeProject3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { Vec2 } from "geom/vec2";
import { vec2Dot, vec2Normalize } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Cross } from "geom/vec3";

export function sectionViewWithMirror(
  sectionView: Pick<SectionViewGeometry, "startX" | "startY" | "endX" | "endY">,
  mirror: boolean,
  mirrorTranslationX: number,
): Pick<SectionViewGeometry, "startX" | "startY" | "endX" | "endY"> {
  if (mirror) {
    function mirrorX(x: number) {
      return -x + mirrorTranslationX;
    }
    return {
      startX: mirrorX(sectionView.endX),
      startY: sectionView.endY,
      endX: mirrorX(sectionView.startX),
      endY: sectionView.startY,
    };
  }
  return {
    startX: sectionView.startX,
    startY: sectionView.startY,
    endX: sectionView.endX,
    endY: sectionView.endY,
  };
}

function sectionViewPlaneU(sectionView: SectionViewGeometry, mirror: boolean, mirrorTranslationX: number): Vec2 {
  const { startX, startY, endX, endY } = sectionViewWithMirror(sectionView, mirror, mirrorTranslationX);
  const sectionViewUx = endX - startX;
  const sectionViewUy = endY - startY;
  if (sectionViewUx !== 0 || sectionViewUy !== 0) {
    return vec2Normalize(sectionViewUx, sectionViewUy);
  }
  return [0, 0];
}

export function sectionViewFrontPlane(
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): Plane | undefined {
  const [sectionPlaneUx, sectionPlaneUy] = sectionViewPlaneU(sectionView, mirror, mirrorTranslationX);
  const { startX, startY } = sectionViewWithMirror(sectionView, mirror, mirrorTranslationX);
  if (sectionPlaneUx !== 0 || sectionPlaneUy !== 0) {
    const plane = planeNew(startX, startY, sectionView.startZ, sectionPlaneUx, sectionPlaneUy, 0, 0, 0, 1);
    const [fixedOriginX, fixedOriginY, fixedOriginZ] = planeProject3(plane, 0, 0, 0);
    plane.originX = fixedOriginX;
    plane.originY = fixedOriginY;
    plane.originZ = fixedOriginZ;
    return plane;
  }
  return undefined;
}

export function sectionViewAnnotationMirrorTranslationX(
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): number {
  if (!mirror) {
    return 0;
  }
  // Project original section view startX/startY to ray crossing origin. This is the reference coordinate system for annotation placement.
  const [sectionViewUx, sectionViewUy] = sectionViewPlaneU(sectionView, false, 0);
  const sectionViewReferencePoint = vec2Dot(sectionView.startX, sectionView.startY, sectionViewUx, sectionViewUy);
  // endX / endY on the mirrored section view corresponds to startX / startY on the original section view.
  // Project mirrored section view endX/endY to ray crossing origin. This is the reference coordinate system for annotation placement after mirroring.
  const mirroredSectionView = sectionViewWithMirror(sectionView, true, mirrorTranslationX);
  const [mirroredSectionViewU, mirroredSectionViewV] = sectionViewPlaneU(sectionView, mirror, mirrorTranslationX);
  const mirroredSectionViewReferencePoint = vec2Dot(
    mirroredSectionView.endX,
    mirroredSectionView.endY,
    mirroredSectionViewU,
    mirroredSectionViewV,
  );

  // We've now computed the original and mirrored points in the plane that defines the annotation coordinate system.
  // We get the mirror translation value to use by substracting the original point after negation from the mirrored point.
  return sectionViewReferencePoint - -mirroredSectionViewReferencePoint;
}

export function sectionViewViewVector(
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): Vec3 {
  const [sectionPlaneUx, sectionPlaneUy] = sectionViewPlaneU(sectionView, mirror, mirrorTranslationX);
  if (sectionPlaneUx !== 0 || sectionPlaneUy !== 0) {
    const viewDir = vec3Cross(sectionPlaneUx, sectionPlaneUy, 0, 0, 0, 1);
    viewDir[0] *= -1;
    viewDir[1] *= -1;
    viewDir[2] *= -1;
    return viewDir; // Note that this results vec3Result.
  }
  return [0, 0, 0];
}

/**
 * @param sectionView
 * @returns the left, right, top, bottom and back clipping planes of a section view.
 */
export function sectionViewSecondaryClipPlanes(
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): Plane[] {
  const { depth } = sectionView;
  const frontPlane = sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX);
  if (frontPlane) {
    const secondaryPlanes: Plane[] = [];

    const { startX, startY, endX, endY } = sectionViewWithMirror(sectionView, mirror, mirrorTranslationX);

    const [normalX, normalY, normalZ] = planeNormal(frontPlane);
    const planeLeft = planeNew(startX, startY, 0, normalX, normalY, normalZ, 0, 0, 1);
    secondaryPlanes.push(planeLeft);
    const planeRight = planeNew(endX, endY, 0, -normalX, -normalY, -normalZ, 0, 0, 1);
    secondaryPlanes.push(planeRight);

    if (sectionView.startZ !== sectionView.endZ) {
      const bottomZ = Math.min(sectionView.startZ, sectionView.endZ);
      const planeBottom = planeNew(0, 0, bottomZ, 1, 0, 0, 0, -1, 0);
      secondaryPlanes.push(planeBottom);

      const topZ = Math.max(sectionView.startZ, sectionView.endZ);
      const planeTop = planeNew(0, 0, topZ, 1, 0, 0, 0, 1, 0);
      secondaryPlanes.push(planeTop);
    }

    if (depth) {
      const [normalX, normalY, normalZ] = planeNormal(frontPlane);
      secondaryPlanes.push({
        originX: frontPlane.originX - normalX * depth,
        originY: frontPlane.originY - normalY * depth,
        originZ: frontPlane.originZ - normalZ * depth,
        uX: -frontPlane.uX,
        uY: -frontPlane.uY,
        uZ: -frontPlane.uZ,
        vX: frontPlane.vX,
        vY: frontPlane.vY,
        vZ: frontPlane.vZ,
      });
    }
    return secondaryPlanes;
  }
  return [];
}

export function sectionViewAABBOnFrontPlane(
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): AABB2 {
  const frontPlane = sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX);
  if (!frontPlane) {
    return aabb2New(0, 0, 0, 0);
  }

  const { startX, startY, endX, endY } = sectionViewWithMirror(sectionView, mirror, mirrorTranslationX);
  const [x0, y0] = planeProject(frontPlane, startX, startY, sectionView.startZ);
  const [x1, y1] = planeProject(frontPlane, endX, endY, sectionView.endZ);
  const minX = Math.min(x0, x1);
  const minY = Math.min(y0, y1);
  const maxX = Math.max(x0, x1);
  const maxY = Math.max(y0, y1);
  return aabb2New(minX, minY, maxX, maxY);
}
